// src/components/InstallPrompt.js
import React, { useState, useEffect } from 'react';
import { Mountain, Share, X } from 'lucide-react';
import './InstallPrompt.css';

const isIOS = () => {
  const ua = window.navigator.userAgent;
  return /iphone|ipad|ipod/i.test(ua);
};

const isInStandaloneMode = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  window.navigator.standalone === true;

const STORAGE_KEY = 'votergо_install_dismissed';
const DISMISS_DAYS = 30;

const InstallPrompt = () => {
  const [showIOSPrompt, setShowIOSPrompt] = useState(false);
  const [showAndroidPrompt, setShowAndroidPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  useEffect(() => {
    // Don't show if already installed
    if (isInStandaloneMode()) return;

    // Don't show if recently dismissed
    const dismissed = localStorage.getItem(STORAGE_KEY);
    if (dismissed) {
      const dismissedDate = new Date(dismissed);
      const daysSince = (new Date() - dismissedDate) / (1000 * 60 * 60 * 24);
      if (daysSince < DISMISS_DAYS) return;
    }

    // iOS — show manual instructions
    if (isIOS()) {
      setTimeout(() => setShowIOSPrompt(true), 3000);
      return;
    }

    // Android/Chrome — listen for native install prompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setTimeout(() => setShowAndroidPrompt(true), 3000);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, new Date().toISOString());
    setShowIOSPrompt(false);
    setShowAndroidPrompt(false);
  };

  const installAndroid = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const result = await deferredPrompt.userChoice;
    if (result.outcome === 'accepted') {
      setShowAndroidPrompt(false);
    }
    setDeferredPrompt(null);
  };

  if (showIOSPrompt) {
    return (
      <div className="install-prompt">
        <div className="install-prompt-content">
          <div className="install-prompt-icon"><Mountain size={20} color="currentColor" style={{ verticalAlign: 'middle' }} /></div>
          <div className="install-prompt-text">
            <div className="install-prompt-title">Add VoterGO to your home screen</div>
            <div className="install-prompt-desc">
              Tap <strong>Share</strong> <span className="share-icon"><Share size={16} color="currentColor" style={{ verticalAlign: 'middle' }} /></span> then <strong>"Add to Home Screen"</strong> for quick access
            </div>
          </div>
          <button className="install-prompt-close" onClick={dismiss}><X size={16} color="currentColor" style={{ verticalAlign: 'middle' }} /></button>
        </div>
      </div>
    );
  }

  if (showAndroidPrompt) {
    return (
      <div className="install-prompt">
        <div className="install-prompt-content">
          <div className="install-prompt-icon"><Mountain size={20} color="currentColor" style={{ verticalAlign: 'middle' }} /></div>
          <div className="install-prompt-text">
            <div className="install-prompt-title">Add VoterGO to your home screen</div>
            <div className="install-prompt-desc">Install for quick access — works like a native app</div>
          </div>
          <button className="install-prompt-install" onClick={installAndroid}>Install</button>
          <button className="install-prompt-close" onClick={dismiss}><X size={16} color="currentColor" style={{ verticalAlign: 'middle' }} /></button>
        </div>
      </div>
    );
  }

  return null;
};

export default InstallPrompt;
